import { CategoryFieldName, TDatabaseId } from '@infotech-soe/metaschema';

import {
  TDatasetTotalsConfigFront,
  TOTALS_FORMULA_ATTACHMENT,
  TOTALS_FORMULA_TYPE,
} from '../../models/user-config/DisplayModeConfig';
import { DatasetGridRow } from '../../services/dataset/view-model/DatasetGridRow';

export const computeSelectedTotals = (
  rows: DatasetGridRow[],
  selectedIds: TDatabaseId[],
  rowIdField: CategoryFieldName,
  totals: Record<string, number> = {},
  config?: TDatasetTotalsConfigFront
): Record<string, number> => {
  if (!config) return totals;

  const selectedFields = new Set<string>();
  for (const [field, rules] of Object.entries(config)) {
    if (
      rules.some(
        ([, , attachment]) => attachment === TOTALS_FORMULA_ATTACHMENT.SELECTED
      )
    ) {
      selectedFields.add(field);
    }
  }

  if (!selectedFields.size) return totals;

  const accMap: Record<
    string,
    { sum: number; count: number; min: number; max: number }
  > = {};
  const selectedSet = new Set(selectedIds);

  if (selectedIds.length > 0) {
    for (const row of rows) {
      if (!selectedSet.has(row[rowIdField])) continue;

      for (const field of selectedFields) {
        const val =
          typeof row[field] === 'number' ? row[field] : Number(row[field]);
        if (!Number.isFinite(val)) continue;

        if (!accMap[field]) {
          accMap[field] = { sum: 0, count: 0, min: Infinity, max: -Infinity };
        }

        const acc = accMap[field];
        acc.sum += val;
        acc.count += 1;
        if (val < acc.min) acc.min = val;
        if (val > acc.max) acc.max = val;
      }
    }
  }

  const result: Record<string, number> = { ...totals };

  for (const [field, rules] of Object.entries(config)) {
    for (const [id, formula, attachment] of rules) {
      if (attachment !== TOTALS_FORMULA_ATTACHMENT.SELECTED) continue;

      const acc = accMap[field];
      let value = 0;

      if (acc?.count) {
        switch (formula) {
          case TOTALS_FORMULA_TYPE.SUM:
            value = acc.sum;
            break;
          case TOTALS_FORMULA_TYPE.AVG:
            value = acc.sum / acc.count;
            break;
          case TOTALS_FORMULA_TYPE.MIN:
            value = acc.min;
            break;
          case TOTALS_FORMULA_TYPE.MAX:
            value = acc.max;
            break;
        }
      }

      result[id] = value;
    }
  }

  return result;
};
